import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    ScoreTrace,
    QuestionnaireTraceBreakdown,
    LabTraceBreakdown,
} from '../../../services/lyfis.service';

interface EvaluatedScenario {
    scenarioName: string;
    condition: string;
    matched: boolean;
}

interface TraceData {
    scenarioId?: string;
    diseaseId?: string;
    diseaseName?: string;
    drsScore?: number;
    pdhScore?: number;
    band?: string;
    trace: ScoreTrace;
    questionnaireFacts?: Record<string, unknown>;
    labMarkers?: Record<string, { value: number; reportDate?: string }>;
}

type MatchedFilterValue = 'all' | 'matched' | 'notMatched';

@Component({
    selector: 'app-lyfis-trace-detail',
    templateUrl: './lyfis-trace-detail.component.html',
})
export class LyfisTraceDetailComponent implements OnInit {
    traceData: TraceData | null = null;
    error: string | null = null;

    // Scenario filter
    scenarioMatchedFilter: MatchedFilterValue = 'all';
    filteredScenarios: EvaluatedScenario[] = [];

    // Questionnaire filter and sort
    questionnaireMatchedFilter: MatchedFilterValue = 'all';
    questionnairePointsSort: 'ascend' | 'descend' | null = null;
    filteredQuestionnaire: QuestionnaireTraceBreakdown[] = [];

    // Lab filter and sort
    labMatchedFilter: MatchedFilterValue = 'all';
    labPointsSort: 'ascend' | 'descend' | null = null;
    filteredLab: LabTraceBreakdown[] = [];

    matchedFilterOptions = [
        { label: 'All', value: 'all' },
        { label: 'Matched Only', value: 'matched' },
        { label: 'Not Matched', value: 'notMatched' },
    ];

    constructor(private route: ActivatedRoute) { }

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            // Primary method: read from localStorage using key
            const key = params['key'];
            if (key) {
                try {
                    const stored = localStorage.getItem(key);
                    if (stored) {
                        this.traceData = JSON.parse(stored);
                        // Clean up localStorage after reading (optional: keep for refresh)
                        // localStorage.removeItem(key);
                        this.initializeFilteredData();
                        return;
                    }
                } catch (e) {
                    console.error('Failed to read trace data from localStorage:', e);
                }
            }

            // Fallback: try reading from data param (for small payloads or backwards compat)
            const data = params['data'];
            if (data) {
                try {
                    this.traceData = JSON.parse(data);
                    this.initializeFilteredData();
                } catch {
                    try {
                        const decoded = decodeURIComponent(data);
                        this.traceData = JSON.parse(decoded);
                        this.initializeFilteredData();
                    } catch (e) {
                        this.error = 'Failed to parse trace data from URL';
                        console.error('Failed to parse trace data:', e);
                    }
                }
            } else if (!key) {
                this.error = 'No trace data provided';
            } else {
                this.error = 'Trace data expired or not found. Please try again from the testing page.';
            }
        });
    }

    initializeFilteredData(): void {
        if (!this.traceData) return;
        this.applyScenarioFilter();
        this.applyQuestionnaireFilter();
        this.applyLabFilter();
    }

    // Scenario filtering
    applyScenarioFilter(): void {
        const scenarios = this.traceData?.trace?.scenarioTrace?.evaluatedScenarios || [];
        this.filteredScenarios = this.filterByMatched(scenarios, this.scenarioMatchedFilter);
    }

    onScenarioFilterChange(filter: MatchedFilterValue): void {
        this.scenarioMatchedFilter = filter;
        this.applyScenarioFilter();
    }

    // Questionnaire filtering and sorting
    applyQuestionnaireFilter(): void {
        let data = [...(this.traceData?.trace?.questionnaireBreakdown || [])];
        data = this.filterByMatched(data, this.questionnaireMatchedFilter);
        if (this.questionnairePointsSort) {
            data = this.sortByPoints(data, this.questionnairePointsSort);
        }
        this.filteredQuestionnaire = data;
    }

    onQuestionnaireFilterChange(filter: MatchedFilterValue): void {
        this.questionnaireMatchedFilter = filter;
        this.applyQuestionnaireFilter();
    }

    onQuestionnairePointsSort(sort: string | null): void {
        this.questionnairePointsSort = sort as 'ascend' | 'descend' | null;
        this.applyQuestionnaireFilter();
    }

    // Lab filtering and sorting
    applyLabFilter(): void {
        let data = [...(this.traceData?.trace?.labBreakdown || [])];
        data = this.filterByMatched(data, this.labMatchedFilter);
        if (this.labPointsSort) {
            data = this.sortByPoints(data, this.labPointsSort);
        }
        this.filteredLab = data;
    }

    onLabFilterChange(filter: MatchedFilterValue): void {
        this.labMatchedFilter = filter;
        this.applyLabFilter();
    }

    onLabPointsSort(sort: string | null): void {
        this.labPointsSort = sort as 'ascend' | 'descend' | null;
        this.applyLabFilter();
    }

    // Utility methods
    filterByMatched<T extends { matched: boolean }>(data: T[], filter: MatchedFilterValue): T[] {
        if (filter === 'matched') return data.filter((d) => d.matched);
        if (filter === 'notMatched') return data.filter((d) => !d.matched);
        return data;
    }

    sortByPoints<T extends { pointsAwarded: number }>(data: T[], direction: 'ascend' | 'descend'): T[] {
        return data.sort((a, b) =>
            direction === 'ascend'
                ? a.pointsAwarded - b.pointsAwarded
                : b.pointsAwarded - a.pointsAwarded
        );
    }

    getMatchedCount(breakdown: Array<{ matched: boolean }>): number {
        return breakdown?.filter((b) => b.matched).length || 0;
    }

    getAvailableCount(breakdown: Array<{ available: boolean }>): number {
        return breakdown?.filter((b) => b.available).length || 0;
    }

    getTotalPoints(breakdown: Array<{ pointsAwarded: number }>): number {
        return breakdown?.reduce((sum, b) => sum + (b.pointsAwarded || 0), 0) || 0;
    }

    printPage(): void {
        window.print();
    }
}

